import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const Contact: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiry: 'general',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would send the form data to your server here
      console.log('Form submitted:', formData);
      
      // Show success message
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Thank you for your message! We will get back to you soon.',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiry: 'general',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>      <Box sx={{ overflow: 'hidden', pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box textAlign="center" mb={8}>
              <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                Get In{' '}
                <Box
                  component="span"
                  sx={{
                    backgroundImage: 'var(--primary-gradient)',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Touch
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto' }}
              >
                We'd love to hear from you. Our team is always here to help and answer any questions.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" mb={3}>
                    Contact Information
                  </Typography>
                  
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box
                      sx={{
                        p: 1.5,
                        mr: 2,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      <LocationIcon />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Our Office
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        123 AkMo Tower, Tech Park, Bangalore, Karnataka, India
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box
                      sx={{
                        p: 1.5,
                        mr: 2,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      <PhoneIcon />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Phone
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            color: theme.palette.primary.main,
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => {
                          const phoneNumber = '916363154142';
                          const whatsappUrl = `https://wa.me/${phoneNumber}`;
                          window.open(whatsappUrl, '_blank');
                        }}
                      >
                        +91 6363 15 4142
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={4}>
                    <Box
                      sx={{
                        p: 1.5,
                        mr: 2,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      <EmailIcon />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Email
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        contact@akmobooks.com
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Follow Us
                  </Typography>
                  
                  <Box display="flex" gap={1}>
                    <IconButton
                      aria-label="Facebook"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                    <IconButton
                      aria-label="Twitter"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      <TwitterIcon />
                    </IconButton>
                    <IconButton
                      aria-label="LinkedIn"
                      onClick={() => window.open('https://linkedin.com/in/akmofilings', '_blank')}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      <LinkedInIcon />
                    </IconButton>
                    <IconButton
                      aria-label="Instagram"
                      onClick={() => window.open('https://instagram.com/akmotechnologies.in', '_blank')}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      <InstagramIcon />
                    </IconButton>
                    <IconButton
                      aria-label="WhatsApp"
                      onClick={() => {
                        const phoneNumber = '916363154142';
                        const whatsappUrl = `https://wa.me/${phoneNumber}`;
                        window.open(whatsappUrl, '_blank');
                      }}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Box>
                  
                  <Box mt={6} mb={2}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Working Hours
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monday - Friday: 9:00 AM - 6:00 PM
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Saturday: 10:00 AM - 2:00 PM
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sunday: Closed
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" mb={3}>
                    Send Us a Message
                  </Typography>
                  
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          error={!!errors.name}
                          helperText={errors.name}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          error={!!errors.email}
                          helperText={errors.email}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          error={!!errors.phone}
                          helperText={errors.phone}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Inquiry Type"
                          name="inquiry"
                          value={formData.inquiry}
                          onChange={handleInputChange}
                          variant="outlined"
                        >
                          <MenuItem value="general">General Inquiry</MenuItem>
                          <MenuItem value="sales">Sales</MenuItem>
                          <MenuItem value="support">Technical Support</MenuItem>
                          <MenuItem value="billing">Billing</MenuItem>
                          <MenuItem value="partnership">Partnership</MenuItem>
                          <MenuItem value="feedback">Feedback</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Your Message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          error={!!errors.message}
                          helperText={errors.message}
                          variant="outlined"
                          multiline
                          rows={5}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<SendIcon />}
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: '50px',
                            backgroundImage: 'var(--primary-gradient)',
                            '&:hover': {
                              boxShadow: '0 8px 25px rgba(71, 118, 230, 0.3)',
                            },
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
          
        </Container>
      </Box>

      {/* Footer */}      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity as 'success' | 'error'} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Contact; 